/**
 * Shared skill types for API contracts between frontend and backend
 * Combines BaseSkill and EmployeeSkill types
 */
import { z } from "zod";
import { nameSchema, uuidSchema } from "./validation-common.js";
// ===== BASE SKILL TYPES =====
// Zod schemas for validation
export const CreateBaseSkillInputSchema = z.object({
    name: nameSchema,
    description: z.string().optional(),
    canExpire: z.boolean().optional(),
    companyId: uuidSchema,
});
export const UpdateBaseSkillInputSchema = z.object({
    name: nameSchema.optional(),
    description: z.string().optional(),
    canExpire: z.boolean().optional(),
});
export const BaseSkillIdParamSchema = z.object({
    id: uuidSchema,
});
// Zod schema for BaseSkill API response
export const BaseSkillResponseSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    name: z.string(),
    description: z.string().nullable(),
    canExpire: z.boolean(),
    companyId: z.uuid(),
});
// ===== EMPLOYEE SKILL TYPES =====
// Zod schemas for employee skills
export const CreateEmployeeSkillInputSchema = z.object({
    employeeId: uuidSchema,
    baseSkillId: uuidSchema,
    expirationDate: z.coerce.date().optional(),
    skillVerified: z.boolean().optional(),
    certificationUrl: z.string().url().optional(),
});
export const UpdateEmployeeSkillInputSchema = z.object({
    expirationDate: z.coerce.date().nullable().optional(),
    skillVerified: z.boolean().optional(),
    certificationUrl: z.string().url().nullable().optional(),
});
export const EmployeeSkillIdParamSchema = z.object({
    id: uuidSchema,
});
// Zod schema for EmployeeSkill API response
export const EmployeeSkillResponseSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    employeeId: z.uuid(),
    baseSkillId: z.uuid(),
    expirationDate: z.iso.datetime().nullable(),
    skillVerified: z.boolean(),
    certificationUrl: z.string().nullable(),
    baseSkill: z
        .object({
        id: z.uuid(),
        name: z.string(),
        description: z.string().nullable(),
        canExpire: z.boolean(),
    })
        .optional(), // Optional because some queries might not include it
});
// ===== SHIFT SKILL TYPES =====
// Zod schemas for shift skills
export const AssignSkillsToShiftInputSchema = z.object({
    baseSkillIds: z.array(uuidSchema).min(1), // Bulk assign
});
export const ShiftSkillIdParamSchema = z.object({
    shiftId: uuidSchema,
    baseSkillId: uuidSchema,
});
// Zod schema for ShiftSkillResponse (oRPC contract)
export const ShiftSkillResponseSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    shiftId: z.uuid(),
    baseSkillId: z.uuid(),
    baseSkill: BaseSkillResponseSchema,
});
